import { ReasonCode, Remark } from '@/services/api/flightChanges';

export interface ChangeReasonData {
  initialReasonCodeId: number;
  reasonCode: string;
  remarkOptions: Remark[];
  isInitialRemarkRequired: boolean;
}


export const CASE_STATUS_ACTIONS = {
  SEND_NOTIFICATION: 'sendNotification',
  SAVE_ONLY: 'saveOnly',
  ON_HOLD: 'onHold',
  NOT_APPLICABLE: 'notApplicable',
} as const;

export type CaseStatusAction = typeof CASE_STATUS_ACTIONS[keyof typeof CASE_STATUS_ACTIONS];


export const ACTION_TO_BACKEND_STATUS: Partial<Record<CaseStatusAction, string>> = {
  [CASE_STATUS_ACTIONS.SEND_NOTIFICATION]: 'TO_NOTIFY',
  [CASE_STATUS_ACTIONS.ON_HOLD]: 'ON_HOLD',
  [CASE_STATUS_ACTIONS.NOT_APPLICABLE]: 'NOT_APPLICABLE',
};

const NOT_APPLICABLE_REASONS_I18N_BASE = 'flightChangeDetails.notApplicableReasons';

const NOT_APPLICABLE_REASONS_IDS = [
  'itinerary_zz',
  'awb_cancelled',
  'flight_number_change',
  'wrong_flight_assignment',
  'manual_manifest',
  'hold_authority',
  'hold_customer',
  'hold_latam_auth',
  'customer_reason',
  'revenue_management',
  'no_contact',
  'case_regularization',
  'operational_irregularity',
  'non_notifiable_rc',
  'reservation_changes_no_flight_change',
  'flight_schedule_change',
  'routing_changes_same_offer',
  'others',
] as const;

export type NotApplicableReasonId = (typeof NOT_APPLICABLE_REASONS_IDS)[number];

export const NOT_APPLICABLE_REASONS: Record<NotApplicableReasonId, string> = Object.fromEntries(
  NOT_APPLICABLE_REASONS_IDS.map((id) => [id, `${NOT_APPLICABLE_REASONS_I18N_BASE}.${id}`])
) as Record<NotApplicableReasonId, string>;

export const NOT_APPLICABLE_REASONS_LIST: ReadonlyArray<{ id: NotApplicableReasonId; labelKey: string }> =
  NOT_APPLICABLE_REASONS_IDS.map((id) => ({ id, labelKey: NOT_APPLICABLE_REASONS[id] }));

/** Id enviado a `NotApplicableModal` cuando el motivo se elige fuera del dropdown (p. ej. RC no notificable). */
export const NOT_APPLICABLE_NON_NOTIFIABLE_RC_ID: NotApplicableReasonId = 'non_notifiable_rc';

export const CASE_STATUS_TO_BACKEND: Record<string, string> = {
  'flightChangesCases.caseStatus.pending': 'PENDING',
  'flightChangesCases.caseStatus.onHold': 'ON_HOLD',
  'flightChangesCases.caseStatus.inAnalysis': 'IN_ANALYSIS',
  'flightChangesCases.caseStatus.notApplicable': 'NOT_APPLICABLE',
  'flightChangesCases.caseStatus.sendingError': 'SENDING_ERROR',
  'flightChangesCases.caseStatus.notified': 'NOTIFIED',
  'flightChangesCases.caseStatus.toNotify': 'TO_NOTIFY',
};

export interface ChangeReasonPayload {
  initialReasonCodeId: number;
  isInitialReasonCodeCorrect: boolean;
  initialRemarkId?: number | null;
  newReasonCodeId?: number | null;
  newRemarkId?: number | null;
}

export interface ChangeCodeFormData {
  action: CaseStatusAction;
  notes?: string | null;
  reasonNotApplicable?: string;
  changeReason: ChangeReasonPayload;
}

export interface SavedFormValues {
  changeReason: {
    isInitialReasonCodeCorrect: boolean;
    initialRemarkId: number | null;
    newReasonCodeId: number | null;
    newRemarkId: number | null;
  };
  notes: string;
  reasonNotApplicable: string;
}

export interface ChangeCodeFormProps {
  changeReason: ChangeReasonData;
  contacts: string[];
  reasonCodes: ReasonCode[];
  onClose: () => void;
  /**
   * Persist flow for the case. Implementations may return a Promise; modal confirm paths await it.
   * Reject the promise when the user should stay on the screen with an error (so submitting state clears).
   */
  onSave: (data: ChangeCodeFormData) => void | Promise<void>;
  readOnly?: boolean;
  saveOnlySuccessMessage?: string | null;
  onClearSuccessMessage?: () => void;
  onHasChangesRef?: (hasChanges: () => boolean) => void;
  savedValues?: SavedFormValues | null;
}

